import { useEffect, useRef, useCallback } from "react";
import { TerraDraw, TerraDrawRectangleMode, TerraDrawCircleMode, TerraDrawPolygonMode, TerraDrawSelectMode, GeoJSONStoreFeatures } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

export type TerraDrawMode = "rectangle" | "circle" | "polygon" | "select" | null;

interface ShapeStyles {
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
  fillOpacity?: number;
}

interface UseTerraDraw {
  map: google.maps.Map | null;
  mode: TerraDrawMode;
  styles?: ShapeStyles;
  onShapeChange?: (features: GeoJSONStoreFeatures[]) => void;
  onModeChange?: (mode: TerraDrawMode) => void;
}

const DEFAULT_STYLES: ShapeStyles = {
  strokeColor: "#0284c7",
  strokeWidth: 2,
  fillColor: "#0284c7",
  fillOpacity: 0.35,
};

// Convert hex color to rgba for TerraDraw
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function useTerraDraw({
  map,
  mode,
  styles = DEFAULT_STYLES,
  onShapeChange,
  onModeChange,
}: UseTerraDraw) {
  const terraDrawRef = useRef<TerraDraw | null>(null);
  const onShapeChangeRef = useRef(onShapeChange);
  const onModeChangeRef = useRef(onModeChange);
  const pendingModeRef = useRef<TerraDrawMode>(mode);

  // Keep refs updated
  onShapeChangeRef.current = onShapeChange;
  onModeChangeRef.current = onModeChange;
  pendingModeRef.current = mode;

  // Initialize TerraDraw
  useEffect(() => {
    if (!map || !window.google?.maps) {
      return;
    }

    // Ensure the map's div element is available before initializing TerraDraw
    const mapDiv = map.getDiv();
    if (!mapDiv) {
      return;
    }

    // Clean up existing instance
    if (terraDrawRef.current) {
      terraDrawRef.current.stop();
      terraDrawRef.current = null;
    }

    // Function to initialize TerraDraw after map projection is ready
    const initTerraDraw = () => {
      try {
        const fillColor = hexToRgba(
          styles.fillColor ?? DEFAULT_STYLES.fillColor ?? "#0284c7",
          styles.fillOpacity ?? DEFAULT_STYLES.fillOpacity ?? 0.35
        );
        const strokeColor = styles.strokeColor ?? DEFAULT_STYLES.strokeColor ?? "#0284c7";
        const strokeWidth = styles.strokeWidth ?? DEFAULT_STYLES.strokeWidth ?? 2;

        // Common style configuration for all modes
        const modeStyles = {
          fillColor,
          outlineColor: strokeColor,
          outlineWidth: strokeWidth,
        };

        const selectStyles = {
          ...modeStyles,
          // Styles for selected features
          selectedFillColor: hexToRgba("#f97316", 0.4), // Orange fill when selected
          selectedOutlineColor: "#f97316", // Orange outline when selected
          selectedOutlineWidth: 3,
          // Point styles for drag handles
          selectedPointColor: "#f97316",
          selectedPointWidth: 10,
          selectedPointOutlineColor: "#ffffff",
          selectedPointOutlineWidth: 2,
          // Midpoint styles
          midPointColor: "#f97316",
          midPointWidth: 6,
          midPointOutlineColor: "#ffffff",
          midPointOutlineWidth: 2,
        };

        const terraDraw = new TerraDraw({
          adapter: new TerraDrawGoogleMapsAdapter({
            map,
            lib: google.maps,
            coordinatePrecision: 9,
            minPixelDragDistance: 1,
          }),
          modes: [
            new TerraDrawRectangleMode({
              styles: modeStyles,
            }),
            new TerraDrawCircleMode({
              styles: modeStyles,
            }),
            new TerraDrawPolygonMode({
              styles: modeStyles,
            }),
            new TerraDrawSelectMode({
              styles: selectStyles,
              pointerDistance: 40,
              allowManualSelection: true,
              allowManualDeselection: true,
              flags: {
                rectangle: {
                  feature: {
                    draggable: true,
                    coordinates: {
                      midpoints: true,
                      draggable: true,
                      resizable: "opposite",
                    },
                  },
                },
                circle: {
                  feature: {
                    draggable: true,
                    coordinates: {
                      midpoints: false,
                      draggable: true,
                      resizable: "center",
                    },
                  },
                },
                polygon: {
                  feature: {
                    draggable: true,
                    coordinates: {
                      midpoints: true,
                      draggable: true,
                    },
                  },
                },
              },
            }),
          ],
        });

        terraDraw.start();

        // Listen for shape changes
        terraDraw.on("change", () => {
          const snapshot = terraDraw.getSnapshot();
          onShapeChangeRef.current?.(snapshot);
        });

        // Listen for drawing finish (when a shape is completed)
        terraDraw.on("finish", () => {
          const snapshot = terraDraw.getSnapshot();
          onShapeChangeRef.current?.(snapshot);
          // Auto-switch to select mode after drawing
          if (terraDraw.getMode() !== "select") {
            terraDraw.setMode("select");
            onModeChangeRef.current?.("select");
          }
        });

        // Listen for the ready event before applying mode
        terraDraw.on("ready", () => {
          if (pendingModeRef.current) {
            try {
              terraDraw.setMode(pendingModeRef.current);
            } catch {
              // Mode might not exist, ignore
            }
          }
        });

        terraDrawRef.current = terraDraw;
      } catch (error) {
        console.error("Failed to initialize TerraDraw:", error);
      }
    };

    // Track cleanup state
    let isCleanedUp = false;
    let rafId: number | null = null;
    let projectionListener: google.maps.MapsEventListener | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 100; // ~1.6 seconds at 60fps

    // Wait for the map to be fully ready with its DOM structure.
    // Google Maps needs both projection AND its internal DOM elements ready.
    // Note: The z-index:3 selector matches terra-draw-google-maps-adapter's
    // getMapEventElement() implementation. This is fragile but necessary to
    // match the adapter's expectations for event handling.
    const waitForMapReady = () => {
      if (isCleanedUp) return;

      attempts++;
      const eventElement = mapDiv.querySelector('div[style*="z-index: 3"]');
      const hasProjection = !!map.getProjection();

      if (eventElement && hasProjection) {
        initTerraDraw();
      } else if (attempts >= MAX_ATTEMPTS) {
        // Fallback: initialize anyway if we've waited long enough
        if (hasProjection) {
          initTerraDraw();
        }
      } else {
        rafId = requestAnimationFrame(waitForMapReady);
      }
    };

    // Check if projection is already available
    if (map.getProjection()) {
      rafId = requestAnimationFrame(waitForMapReady);
    } else {
      // Wait for projection to be available
      projectionListener = map.addListener("projection_changed", () => {
        if (projectionListener) {
          google.maps.event.removeListener(projectionListener);
          projectionListener = null;
        }
        rafId = requestAnimationFrame(waitForMapReady);
      });
    }

    return () => {
      isCleanedUp = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (projectionListener) {
        google.maps.event.removeListener(projectionListener);
      }
      if (terraDrawRef.current) {
        terraDrawRef.current.stop();
        terraDrawRef.current = null;
      }
    };
  }, [map, styles.fillColor, styles.fillOpacity, styles.strokeColor, styles.strokeWidth]);

  // Handle mode changes
  useEffect(() => {
    if (!terraDrawRef.current) return;

    try {
      // When mode is null, switch to select mode to exit drawing
      terraDrawRef.current.setMode(mode ?? "select");
    } catch {
      // Mode might not exist, ignore
    }
  }, [mode]);

  // Clear all shapes
  const clear = useCallback(() => {
    if (!terraDrawRef.current) return;
    terraDrawRef.current.clear();
  }, []);

  // Get current shapes
  const getSnapshot = useCallback(() => {
    if (!terraDrawRef.current) return [];
    return terraDrawRef.current.getSnapshot();
  }, []);

  // Set mode
  const setMode = useCallback((newMode: TerraDrawMode) => {
    if (!terraDrawRef.current || !newMode) return;
    try {
      terraDrawRef.current.setMode(newMode);
    } catch {
      // Mode might not exist, ignore
    }
  }, []);

  return {
    clear,
    getSnapshot,
    setMode,
    terraDraw: terraDrawRef.current,
  };
}
